"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import Image from "next/image";
import { motion, AnimatePresence } from "framer-motion";
import {
  useMusicPlayerOptional,
  loadPopupPosition,
  savePopupPosition,
  loadPopupVisible,
  savePopupVisible,
  type RepeatMode,
} from "@/context/MusicPlayerContext";
import { useI18n } from "@/i18n/I18nProvider";

function cycleRepeat(mode: RepeatMode): RepeatMode {
  if (mode === "off") return "one";
  if (mode === "one") return "all";
  return "off";
}

function formatTime(sec: number): string {
  if (!Number.isFinite(sec) || sec < 0) return "0:00";
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

const POPUP_W = 280;
const POPUP_H = 380;
const PADDING = 16;

function clampPositionToViewport(x: number, y: number): { x: number; y: number } {
  if (typeof window === "undefined") return { x, y };
  const maxX = Math.max(0, window.innerWidth - POPUP_W - PADDING);
  const maxY = Math.max(0, window.innerHeight - POPUP_H - PADDING);
  return {
    x: Math.max(0, Math.min(maxX, x)),
    y: Math.max(0, Math.min(maxY, y)),
  };
}

export function MusicPlayerFloatingPopup() {
  const player = useMusicPlayerOptional();
  const { t } = useI18n();
  const [position, setPosition] = useState(() => {
    const loaded = loadPopupPosition();
    return clampPositionToViewport(loaded.x, loaded.y);
  });
  const [isVisible, setIsVisible] = useState(() => loadPopupVisible());
  const [isDragging, setIsDragging] = useState(false);
  const [isSeeking, setIsSeeking] = useState(false);
  const dragStart = useRef({ x: 0, y: 0, posX: 0, posY: 0 });
  const lastPosition = useRef(loadPopupPosition());
  const progressBarRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    savePopupVisible(isVisible);
  }, [isVisible]);

  // Clamp popup khi resize desktop → mobile (tránh popup nằm ngoài viewport)
  const syncPositionToViewport = useCallback(() => {
    setPosition((prev) => {
      const clamped = clampPositionToViewport(prev.x, prev.y);
      if (clamped.x !== prev.x || clamped.y !== prev.y) {
        savePopupPosition(clamped.x, clamped.y);
        return clamped;
      }
      return prev;
    });
  }, []);

  useEffect(() => {
    // Mount: đồng bộ vị trí từ localStorage (có thể đã lưu từ desktop) về viewport hiện tại
    const loaded = loadPopupPosition();
    const clamped = clampPositionToViewport(loaded.x, loaded.y);
    setPosition(clamped);
    if (clamped.x !== loaded.x || clamped.y !== loaded.y) savePopupPosition(clamped.x, clamped.y);

    const onResize = syncPositionToViewport;
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [syncPositionToViewport]);

  const handleDragStart = useCallback(
    (e: React.MouseEvent | React.TouchEvent) => {
      const clientX = "touches" in e ? e.touches[0].clientX : e.clientX;
      const clientY = "touches" in e ? e.touches[0].clientY : e.clientY;
      dragStart.current = { x: clientX, y: clientY, posX: position.x, posY: position.y };
      setIsDragging(true);
    },
    [position]
  );

  useEffect(() => {
    if (!isDragging) return;
    const onMove = (e: MouseEvent | TouchEvent) => {
      const clientX = "touches" in e ? (e as TouchEvent).touches[0].clientX : (e as MouseEvent).clientX;
      const clientY = "touches" in e ? (e as TouchEvent).touches[0].clientY : (e as MouseEvent).clientY;
      const dx = clientX - dragStart.current.x;
      const dy = clientY - dragStart.current.y;
      let newX = dragStart.current.posX + dx;
      let newY = dragStart.current.posY + dy;
      const maxX = typeof window !== "undefined" ? window.innerWidth - POPUP_W - PADDING : 400;
      const maxY = typeof window !== "undefined" ? window.innerHeight - POPUP_H - PADDING : 600;
      newX = Math.max(0, Math.min(maxX, newX));
      newY = Math.max(0, Math.min(maxY, newY));
      lastPosition.current = { x: newX, y: newY };
      setPosition({ x: newX, y: newY });
    };
    const onEnd = () => {
      setIsDragging(false);
      savePopupPosition(lastPosition.current.x, lastPosition.current.y);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onEnd);
    window.addEventListener("touchmove", onMove, { passive: true });
    window.addEventListener("touchend", onEnd);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onEnd);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onEnd);
    };
  }, [isDragging]);

  const getPercentFromEvent = useCallback((clientX: number) => {
    if (!progressBarRef.current) return 0;
    const rect = progressBarRef.current.getBoundingClientRect();
    return (clientX - rect.left) / rect.width;
  }, []);

  const handleThumbMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (!player || !player.duration) return;
      setIsSeeking(true);
      player.seekToPercent(getPercentFromEvent(e.clientX));
    },
    [player, getPercentFromEvent]
  );

  const handleThumbTouchStart = useCallback(
    (e: React.TouchEvent) => {
      e.stopPropagation();
      if (!player || !player.duration) return;
      setIsSeeking(true);
      player.seekToPercent(getPercentFromEvent(e.touches[0].clientX));
    },
    [player, getPercentFromEvent]
  );

  useEffect(() => {
    if (!isSeeking || !player) return;
    const onMove = (e: MouseEvent) => player.seekToPercent(getPercentFromEvent(e.clientX));
    const onTouchMove = (e: TouchEvent) => {
      e.preventDefault();
      player.seekToPercent(getPercentFromEvent(e.touches[0].clientX));
    };
    const onEnd = () => setIsSeeking(false);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onEnd);
    window.addEventListener("touchmove", onTouchMove, { passive: false });
    window.addEventListener("touchend", onEnd);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onEnd);
      window.removeEventListener("touchmove", onTouchMove);
      window.removeEventListener("touchend", onEnd);
    };
  }, [isSeeking, player, getPercentFromEvent]);

  if (!player || player.songs.length === 0) return null;

  const handleClose = () => setIsVisible(false);

  const fab = (
    <motion.button
      key="fab"
      initial={{ scale: 0 }}
      animate={{ scale: 1 }}
      exit={{ scale: 0 }}
      onClick={() => setIsVisible(true)}
      className="fixed z-50 flex h-14 w-14 items-center justify-center rounded-full bg-dev-cyan/90 shadow-lg shadow-dev-cyan/30 text-white hover:bg-dev-cyan transition-transform active:scale-95 sm:hover:scale-110 print:hidden bottom-[max(1.5rem,env(safe-area-inset-bottom))] right-[max(1.5rem,env(safe-area-inset-right))]"
      aria-label={t("home.musicPlayer.play")}
    >
      {player.isPlaying ? (
        <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
          <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
        </svg>
      ) : (
        <svg className="h-6 w-6 ml-1" fill="currentColor" viewBox="0 0 24 24">
          <path d="M8 5v14l11-7z" />
        </svg>
      )}
    </motion.button>
  );

  const popup = (
    <motion.div
      key="popup"
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.8 }}
      style={{
        position: "fixed",
        left: position.x,
        top: position.y,
        zIndex: 50,
      }}
      className="w-[280px] max-w-[calc(100vw-2rem)] rounded-2xl border border-dev-text-muted/20 bg-dev-bg/95 backdrop-blur-xl shadow-xl print:hidden"
    >
      {/* Thanh kéo + nút đóng */}
      <div
        className="flex cursor-grab active:cursor-grabbing items-center justify-between rounded-t-2xl border-b border-dev-text-muted/10 bg-dev-text-muted/5 px-3 py-2 select-none"
        onMouseDown={handleDragStart}
        onTouchStart={handleDragStart}
      >
        <span className="text-xs font-medium text-dev-text-muted">🎵 {player.currentSong?.title ?? "—"}</span>
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleClose();
          }}
          className="rounded-full p-1.5 text-dev-text-muted hover:bg-dev-text-muted/20 hover:text-dev-text-primary transition-colors -mr-1"
          aria-label="Đóng"
        >
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <div className="p-4 space-y-3">
        {/* Cover tròn */}
        <div className="flex justify-center">
          <div
            className={`h-20 w-20 sm:h-24 sm:w-24 rounded-full overflow-hidden bg-dev-text-muted/20 ${
              player.isPlaying ? "animate-spin" : ""
            }`}
            style={{ animationDuration: "8s" }}
          >
            {player.discImageSrc ? (
              <Image
                src={player.discImageSrc}
                alt={player.discImageAlt}
                width={96}
                height={96}
                className="h-full w-full object-cover"
                unoptimized
              />
            ) : (
              <div className="flex h-full w-full items-center justify-center text-3xl">🎵</div>
            )}
          </div>
        </div>

        <p className="text-center text-sm font-semibold text-dev-text-primary truncate px-2">
          {player.currentSong?.title ?? "—"}
        </p>
        <p className="text-center text-xs text-dev-text-muted truncate px-2">{player.currentSong?.artist ?? ""}</p>

        {/* Progress + thumb + VOL — VOL cạnh thanh progress */}
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-dev-text-muted w-8 shrink-0 tabular-nums">
            {formatTime(player.currentTime)}
          </span>
          <div
            ref={progressBarRef}
            className="relative flex-1 min-w-0 h-2 min-h-[12px] sm:h-1.5 cursor-pointer rounded-full bg-dev-text-muted/20 overflow-visible"
            onClick={(e) => {
              if ((e.target as HTMLElement).closest("[data-thumb]")) return;
              player.seekFromEvent(e);
            }}
            role="progressbar"
            aria-valuenow={Math.round(player.currentTime)}
            aria-valuemin={0}
            aria-valuemax={Math.round(player.duration || 0)}
            aria-label={t("home.musicPlayer.progress")}
          >
            <div
              className="absolute inset-y-0 left-0 rounded-full bg-dev-cyan/70 transition-[width] duration-100"
              style={{ width: `${player.duration ? (player.currentTime / player.duration) * 100 : 0}%` }}
            />
            <div
              data-thumb
              className="absolute top-1/2 w-3 h-3 -translate-y-1/2 -translate-x-1/2 rounded-full bg-dev-cyan shadow-[0_0_0_2px_rgba(255,255,255,0.3)] cursor-grab active:cursor-grabbing hover:bg-dev-cyan-light touch-none select-none z-10"
              style={{ left: `${player.duration ? (player.currentTime / player.duration) * 100 : 0}%` }}
              onMouseDown={handleThumbMouseDown}
              onTouchStart={handleThumbTouchStart}
              onClick={(e) => e.stopPropagation()}
              role="slider"
              aria-valuenow={Math.round(player.currentTime)}
              aria-valuemin={0}
              aria-valuemax={Math.round(player.duration || 0)}
              tabIndex={0}
            />
          </div>
          <span className="text-[10px] text-dev-text-muted w-8 shrink-0 tabular-nums text-right">
            {formatTime(player.duration || 0)}
          </span>
          {/* VOL dọc — cạnh thanh progress */}
          <div
            className="flex items-center justify-center shrink-0 w-8 h-12"
            title={t("home.musicPlayer.volume")}
          >
            <input
              type="range"
              min="0"
              max="1"
              step="0.05"
              value={player.volume}
              onChange={(e) => player.setVolume(parseFloat(e.target.value))}
              className="popup-volume-vertical cursor-pointer touch-manipulation"
              aria-label={t("home.musicPlayer.volume")}
            />
          </div>
        </div>

        {/* Controls — Shuffle | Prev | Play | Next | Repeat — giống home */}
        <div className="flex items-center justify-center gap-1 sm:gap-2">
          <button
            onClick={() => player.setShuffle(!player.shuffle)}
            className={`p-2 rounded-full transition-colors touch-manipulation ${
              player.shuffle ? "text-dev-cyan" : "text-dev-text-muted hover:text-dev-cyan hover:bg-dev-cyan/10"
            }`}
            aria-label={t("home.musicPlayer.shuffle")}
          >
            <svg className="h-4 w-4 sm:h-5 sm:w-5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M10.59 9.17L5.41 4 4 5.41l5.17 5.17 1.42-1.41zM14.5 4l2.04 2.04-4.96 4.96-2.04-2.04L14.5 4zm3.33 9.5l-1.41 1.41 3.13 3.13L14.5 20H20v-5.5l-2.04 2.04-3.13-3.13 1.41-1.41zM9.36 14.5l1.41-1.41-3.13-3.13L9.5 4H4v5.5l2.04-2.04 3.13 3.13z" />
            </svg>
          </button>
          <button
            onClick={player.goPrev}
            className="min-w-[44px] min-h-[44px] p-2.5 sm:p-2 flex items-center justify-center rounded-full text-dev-text-muted hover:text-dev-cyan hover:bg-dev-cyan/10 active:bg-dev-cyan/20 transition-colors touch-manipulation"
            aria-label={t("home.musicPlayer.prev")}
          >
            <svg className="h-4 w-4 sm:h-5 sm:w-5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M6 6h2v12H6zm3.5 6l8.5 6V6z" />
            </svg>
          </button>
          <button
            onClick={player.togglePlay}
            className="min-w-[48px] min-h-[48px] p-3 flex items-center justify-center rounded-full bg-dev-cyan/25 text-dev-cyan hover:bg-dev-cyan/35 active:bg-dev-cyan/45 transition-colors touch-manipulation"
            aria-label={player.isPlaying ? t("home.musicPlayer.pause") : t("home.musicPlayer.play")}
          >
            {player.isPlaying ? (
              <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
              </svg>
            ) : (
              <svg className="h-5 w-5 ml-0.5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M8 5v14l11-7z" />
              </svg>
            )}
          </button>
          <button
            onClick={player.goNext}
            className="min-w-[44px] min-h-[44px] p-2.5 sm:p-2 flex items-center justify-center rounded-full text-dev-text-muted hover:text-dev-cyan hover:bg-dev-cyan/10 active:bg-dev-cyan/20 transition-colors touch-manipulation"
            aria-label={t("home.musicPlayer.next")}
          >
            <svg className="h-4 w-4 sm:h-5 sm:w-5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M6 18l8.5-6L6 6v12zm2-10v8l5.4-4-5.4-4zm13 4V6h-2v6h2z" />
            </svg>
          </button>
          <button
            onClick={() => player.setRepeatMode(cycleRepeat(player.repeatMode))}
            className={`relative p-2 rounded-full transition-colors touch-manipulation ${
              player.repeatMode !== "off" ? "text-dev-cyan" : "text-dev-text-muted hover:text-dev-cyan hover:bg-dev-cyan/10"
            }`}
            aria-label={t(`home.musicPlayer.repeat.${player.repeatMode}`)}
          >
            <svg className="h-4 w-4 sm:h-5 sm:w-5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M7 7h10v3l4-4-4-4v3H5v6h2V7zm10 10H7v-3l-4 4 4 4v-3h12v-6h-2v4z" />
            </svg>
            {player.repeatMode === "one" && (
              <span className="absolute -bottom-0.5 -right-0.5 flex h-2.5 w-2.5 items-center justify-center rounded bg-dev-cyan text-[7px] font-bold text-white">
                1
              </span>
            )}
          </button>
        </div>
      </div>
    </motion.div>
  );

  return (
    <AnimatePresence mode="wait">
      {isVisible ? popup : (player.isPlaying || player.currentSong) ? fab : null}
    </AnimatePresence>
  );
}
